// Order history (SM-829): one row per finished order of the node's users —
// filled, canceled, rejected, expired or failed, with the reason — in the
// selected currency (the stake leg) and a period of whole local days: the
// last 30 by default, at most 92, always sent as an explicit
// from_unix_ms / to_unix_ms. Direct or subtree as on every Trading page; the
// User picker reads one account's own route. Does not poll.
//
//   GET /api/broker/{id}/orders/history?scope=&currency=&status=&from_unix_ms=&to_unix_ms=&offset=&limit=
//   GET /api/broker/{id}/accounts/{aid}/orders/history?currency=&status=&from_unix_ms=&to_unix_ms=&offset=&limit=
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, type QueryKey } from "@tanstack/react-query";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { EmptyPage, Pager } from "@/components/ui/Common";
import { QueryBlock } from "@/components/ui/States";
import { Select } from "@/components/ui/Field";
import { OrdersTable } from "@/components/OrdersTable";
import {
  TRADING_PAGE_LIMIT,
  TradingFilters,
  WindowPicker,
  validateTradingSearch,
  validateWindowSearch,
  type TradingSearch,
  type WindowSearch,
} from "@/components/TradingFilters";
import { fetchAccountOrderHistory, fetchOrderHistory, keys } from "@/lib/queries";
import { ORDER_HISTORY_STATUSES, parseOrderHistoryStatus, resolveWindow, scopeFromSearch } from "@/lib/trading";
import type { AccountScope, OrderListResponse } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf } from "@/i18n/labels";

interface OrderHistorySearch extends TradingSearch, WindowSearch {
  /** One terminal status (absent = all of them). */
  status?: string;
}

export const Route = createFileRoute("/_app/orders/history")({
  validateSearch: (s: Record<string, unknown>): OrderHistorySearch => ({
    ...validateTradingSearch(s),
    ...validateWindowSearch(s),
    status: parseOrderHistoryStatus(s.status),
  }),
  beforeLoad: capabilityGuard("VIEW"),
  component: OrderHistoryPage,
});

function OrderHistoryPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { nodeId, currency } = useScope();
  const scope: AccountScope = scopeFromSearch(search.scope);
  const account = search.account;
  const now = Date.now();
  const w = resolveWindow(search.from, search.to, now);
  const page = {
    currency,
    status: search.status,
    from_unix_ms: w.from,
    to_unix_ms: w.to,
    offset: search.offset ?? 0,
    limit: TRADING_PAGE_LIMIT,
  };
  const nodeParams = { scope, ...page };
  const probe: { queryKey: QueryKey; queryFn: () => Promise<OrderListResponse> } = account
    ? { queryKey: keys.accountOrderHistory(nodeId, account, page), queryFn: () => fetchAccountOrderHistory(nodeId, account, page) }
    : { queryKey: keys.orderHistory(nodeId, nodeParams), queryFn: () => fetchOrderHistory(nodeId, nodeParams) };
  const q = useQuery({ ...probe, enabled: guard.kind === "allow" });
  const set = (patch: Partial<OrderHistorySearch>) => void navigate({ search: (prev) => ({ ...prev, offset: undefined, ...patch }) });
  const onOffset = (o: number) => set({ offset: o || undefined });
  const hint = t(account ? "trading.emptyHintAccount" : "trading.emptyHintNode");

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("orderHistory.title")}
        subtitle={t("orderHistory.subtitle")}
        coverage={scope === "subtree" ? "subtree" : "node"}
        money
      />
      <SectionCard title={t("orders.historyList")}>
        <TradingFilters scope={scope} account={account} onScope={(v) => set({ scope: v })} onAccount={(a) => set({ account: a })}>
          <div className="w-44">
            <Select
              label={t("common.status")}
              value={search.status ?? ""}
              onChange={(e) => set({ status: e.target.value || undefined })}
              options={[
                { value: "", label: t("common.all") },
                ...ORDER_HISTORY_STATUSES.map((s) => ({ value: s, label: labelOf(t, "orderStatus", s) })),
              ]}
            />
          </div>
        </TradingFilters>
        <WindowPicker key={`${w.from}-${w.to}`} window={w} now={now} onChange={(nw) => set({ from: nw?.from, to: nw?.to })} />
        <QueryBlock
          query={q}
          empty={(d) =>
            (d.orders ?? []).length === 0 ? <EmptyPage title={t("orders.emptyHistory")} hint={hint} page={d.page} onOffset={onOffset} /> : null
          }
        >
          {(d) => (
            <>
              <OrdersTable orders={d.orders ?? []} variant="history" />
              <Pager page={d.page} onOffset={onOffset} />
            </>
          )}
        </QueryBlock>
      </SectionCard>
    </Guarded>
  );
}
